/**
 * Pull the server's `errorCode` out of whatever a failed API call rejected with, so a call site can
 * hand it straight to `getErrorMessage` without reaching through a shape that may not be there.
 *
 * Two shapes reach call sites today, and the difference is not cosmetic:
 *
 *  - **Legacy axios** (`HttpService`) rejects with an error whose `response.data` holds the body —
 *    but ONLY when a response actually arrived. On a network drop, timeout, CORS rejection or a
 *    request cancelled by navigation there is no `response` at all, and the widespread handler that
 *    dereferences `.response` unguarded throws a TypeError *inside the catch block*, swallowing the
 *    real failure and leaving the user with no notification. See
 *    `errorHandlerResponseShape.test.js`, which characterizes that whole class.
 *  - **The generated client** (`src/api/generated`) rejects with the parsed error BODY itself — no
 *    `response` wrapper, ever. `errorCode` sits at the top level, and for a body-less 5xx the
 *    rejection is a bare `{}`. So a migrated service that goes through `.response` crashes on every
 *    single failure, not just the offline ones.
 *
 * Returns `undefined` when no code can be found; `getErrorMessage(undefined)` is total and yields
 * the generic Hebrew message, which is the right outcome for an empty 500.
 */
export const getApiErrorCode = (e: unknown): number | undefined => {
  if (!e || typeof e !== 'object') {
    return undefined;
  }
  const err = e as { errorCode?: number; response?: { data?: { errorCode?: number } } };
  return err.response?.data?.errorCode ?? err.errorCode;
};

type ServerErrorBody = {
  errorMessage?: unknown;
  message?: unknown;
};

type ThrownError = ServerErrorBody & {
  response?: { data?: ServerErrorBody | string };
};

const asMessage = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

/**
 * The server's own words for a failed call, or `fallback`.
 *
 * The companion to {@link getApiErrorCode}, for the endpoints that explain themselves in prose
 * rather than with a numeric code — and it reads the same two rejection shapes for the same reasons
 * documented above. `errorMessage` is the key `RequestExceptionHandler` uses for every validation
 * failure and every 409; `message` is checked too because a Spring default error body carries that
 * one instead.
 *
 * Without this, a service reading `error.response?.data?.message` can never match anything thrown by
 * the generated client, and every failure collapses into the same generic toast — including the ones
 * the server took care to explain, like "this measurement is used by נוסחת היקפים".
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  // A real Error is a transport failure ("Failed to fetch"), not the server explaining itself. Its
  // message is English and means nothing to the person reading the toast, so it stays out.
  if (error instanceof Error) {
    return fallback;
  }

  const err = (error ?? {}) as ThrownError;
  const body = err.response?.data;

  if (typeof body === 'string') {
    return asMessage(body) || fallback;
  }

  return (
    asMessage(err.errorMessage) ||
    asMessage(err.message) ||
    asMessage(body?.errorMessage) ||
    asMessage(body?.message) ||
    fallback
  );
};
